import { Button, Flex, Input, Select, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import HeroImg from "../../../../assets/images/HeaderImg.png";
import HeroReview from "../../../../assets/images/HeroReview.png";

import BookIcon from "../_icons/BookIcon";
import VoiceIcon from "../_icons/VoiceIcon";

// Type definitions for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onstart: ((this: SpeechRecognition, ev: Event) => void) | null;
  onend: ((this: SpeechRecognition, ev: Event) => void) | null;
  onresult:
    | ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => void)
    | null;
  onerror:
    | ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => void)
    | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

type SearchType = "course" | "jobs";

interface SearchOption {
  value: SearchType;
  label: string;
}

export default function Hero() {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState<SearchType>("course");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isListening, setIsListening] = useState<boolean>(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const searchOptions: SearchOption[] = [
    { value: "course", label: "কোর্স" },
    // { value: "jobs", label: "চাকরি" },
  ];

  useEffect(() => {
    // Initialize speech recognition
    const SpeechRecognitionAPI =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognitionAPI) {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "bn-BD"; // Bangla language

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results[0][0].transcript;
        setSearchQuery(transcript);
        // message.success('ভয়েস রেকর্ড সফল হয়েছে');
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);

        if (event.error === "not-allowed") {
          message.error("মাইক্রোফোন অনুমতি প্রয়োজন");
        } else if (event.error === "no-speech") {
          message.warning("কোনো কথা শোনা যায়নি");
        } else {
          message.error("ভয়েস রেকগনিশন ত্রুটি");
        }
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleVoiceClick = (): void => {
    if (!recognitionRef.current) {
      message.error("আপনার ব্রাউজার ভয়েস রেকগনিশন সমর্থন করে না");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      try {
        recognitionRef.current.start();
        message.info("বলা শুরু করুন...");
      } catch (error) {
        console.error("Error starting recognition:", error);
        message.error("মাইক্রোফোন চালু করতে ব্যর্থ");
      }
    }
  };

  const handleSearch = (): void => {
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      message.warning("অনুগ্রহ করে কিছু লিখুন");
      return;
    }

    // Navigate to the appropriate page with search query
    const route = searchType === "course" ? "/courses" : "/jobs";
    navigate(`${route}?search=${encodeURIComponent(trimmedQuery)}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <section className="bg-gradient-to-l from-teal-700 to-gray-800">
      <div className="container grid grid-cols-1 lg:grid-cols-2 items-center gap-2 pt-10">
        {/* LEFT CONTENT */}
        <div className="2xl:pl-14">
          <h1 className="text-theme-white-900 text-4xl xl:text-6xl font-semibold lg:leading-[40px] xl:leading-[90px]">
            বিদেশে ক্যারিয়ার গড়ুন, দক্ষতার সাথে
          </h1>

          <p className="text-theme-white-900 text-xl xl:text-2xl font-medium mt-4 lg:mt-8">
            স্কি<span className="opacity-0 absolute">.</span>ল ডেভেলপ করুন,
            চাকরিতে আবেদন করুন, আর স্বপ্নের কর্মক্ষেত্রে যোগ দিন
          </p>

          {/* SEARCH BAR */}
          <div className="w-full flex flex-wrap justify-start items-center gap-2 lg:gap-4 mt-6">
            <div className="flex items-center bg-white gap-0 px-2 lg:px-3 py-1 lg:py-2 rounded-full">
              {/* SELECT */}
              <Select<SearchType>
                value={searchType}
                onChange={(value: SearchType) => setSearchType(value)}
                options={searchOptions}
                size="large"
                bordered={false}
                className="w-[90px] px-0"
                suffixIcon={null}
                dropdownStyle={{ borderRadius: 12 }}
                optionLabelProp="label"
                labelRender={(option) => (
                  <span className="flex items-center gap-2 text-theme-neutral-800">
                    <BookIcon className="text-theme-error-300" />
                    {option.label}
                  </span>
                )}
              />

              {/* INPUT */}
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`পছন্দের ${
                  searchOptions.find((o) => o.value === searchType)?.label || ""
                } খুঁজুন`}
                className="border-none w-auto antd-input-primary !pl-0"
                size="large"
              />

              {/* BUTTON - Hidden on small devices, shown on right and bottom for large devices */}
              <Button
                type="primary"
                size="large"
                onClick={handleSearch}
                className="hidden lg:block rounded-full font-medium text-sm lg:text-base w-16 lg:w-24 pt-1 ml-auto"
                style={{
                  backgroundColor: "#207E7D",
                  borderColor: "#207E7D",
                }}
              >
                খুঁজুন
              </Button>
            </div>

            {/* VOICE ICON */}
            <div
              className={`w-14 p-3 flex items-center justify-center rounded-full bg-white cursor-pointer border transition-all ${
                isListening
                  ? "border-theme-error-300 shadow-lg"
                  : "border-theme-primary"
              }`}
              onClick={handleVoiceClick}
              role="button"
              aria-label={
                isListening ? "রেকর্ডিং বন্ধ করুন" : "ভয়েস দিয়ে খুঁজুন"
              }
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleVoiceClick();
                }
              }}
            >
              <VoiceIcon
                className={`text-3xl ${
                  isListening ? "text-theme-error-300" : "text-theme-primary"
                }`}
              />
            </div>
          </div>

          {/* REVIEW */}
          <div className="mt-6 mb-6 lg:mb-12 xl:mb-0">
            <Flex justify="start">
              <img src={HeroReview} alt="Review" />
            </Flex>
            <div className="mt-1">
              <span className="text-white text-base font-semibold">
                4.8 / 5.0{" "}
              </span>
              <span className="text-white text-base font-medium">রিভিউ</span>
            </div>
          </div>
        </div>

        {/* RIGHT IMAGE */}
        <div className="flex justify-end h-auto">
          <img
            src={HeroImg}
            alt="Hero"
            className="h-full object-contain hidden lg:block relative xl:-mb-0"
          />
        </div>
      </div>
    </section>
  );
}
